import { inferGraphTimeZone, type GraphDocument } from '../entities/graph-document';
import { currentTimeZone, type JourneyNode } from '../entities/node';
import { canonicalizeNodeReferences } from '../policies/auto-connections/connection-input-normalizer';
import { removeNodeFromRelationships } from '../policies/explicit-relationships';
import type { GraphRepository } from '../ports/graph-repository';
import { DEFAULT_CONNECTION_STRATEGY, type ConnectionStrategy } from '../ports/preferences-repository';
import type { SearchRepository } from '../ports/search-repository';
import { deriveConnections } from '../policies/auto-connections/derive-connections';
import { validateNode } from '../policies/node-validation/validate-node';
export class NodeLifecycleService {
  constructor(private graphRepository: GraphRepository, private searchRepository?: SearchRepository) {}

  async saveNode(graph: GraphDocument, node: JourneyNode, options: { owner: boolean; connectionStrategy?: ConnectionStrategy }): Promise<GraphDocument> {
    if (!options.owner) {
      throw new Error('Read-only viewer cannot edit the owner graph.');
    }

    const next = saveNodeToGraph(graph, node, { connectionStrategy: options.connectionStrategy });
    const savedNode = next.nodes.find((candidate) => candidate.nodeId === node.nodeId);

    await this.graphRepository.save(next);
    if (savedNode) {
      await this.searchRepository?.upsert(savedNode);
    }

    return next;
  }

  async deleteNode(graph: GraphDocument, nodeId: string): Promise<GraphDocument> {
    const next = deleteNodeFromGraph(graph, nodeId);

    await this.graphRepository.save(next);
    await this.searchRepository?.remove(nodeId);

    return next;
  }
}

export function saveNodeToGraph(
  graph: GraphDocument,
  node: JourneyNode,
  options: { connectionStrategy?: ConnectionStrategy } = {},
): GraphDocument {
  const validation = validateNode(node);
  if (!validation.valid) {
    throw new Error(validation.hints.join('; '));
  }

  const updatedAt = new Date().toISOString();
  const timeZone = currentTimeZone();
  const existing = graph.nodes.find((item) => item.nodeId === node.nodeId);
  const savedNode = {
    ...node,
    createdAt: existing?.createdAt ?? node.createdAt,
    createdAtTimeZone: existing?.createdAtTimeZone ?? node.createdAtTimeZone ?? inferGraphTimeZone(graph) ?? timeZone,
    updatedAt,
    updatedAtTimeZone: timeZone,
  };
  const provisionalNodes = [...graph.nodes.filter((candidate) => candidate.nodeId !== node.nodeId), savedNode];
  const canonicalizedNode = canonicalizeNodeReferences(savedNode, provisionalNodes);
  const nodes = [...graph.nodes.filter((candidate) => candidate.nodeId !== node.nodeId), canonicalizedNode];

  return {
    ...graph,
    timestampOriginTimeZone: graph.timestampOriginTimeZone ?? inferGraphTimeZone({ ...graph, nodes }),
    createdAtTimeZone: graph.createdAtTimeZone ?? inferGraphTimeZone(graph),
    updatedAt,
    updatedAtTimeZone: timeZone,
    nodes,
    relationships: graph.relationships ?? [],
    connections: deriveConnections(nodes, updatedAt, {
      strategy: options.connectionStrategy ?? DEFAULT_CONNECTION_STRATEGY,
      timeZone,
    }),
  };
}

export function deleteNodeFromGraph(
  graph: GraphDocument,
  nodeId: string,
  options: { connectionStrategy?: ConnectionStrategy } = {},
): GraphDocument {
  const timeZone = currentTimeZone();
  const updatedAt = new Date().toISOString();
  const nodes = graph.nodes.filter((candidate) => candidate.nodeId !== nodeId);

  return {
    ...graph,
    timestampOriginTimeZone: graph.timestampOriginTimeZone ?? inferGraphTimeZone({ ...graph, nodes }),
    createdAtTimeZone: graph.createdAtTimeZone ?? inferGraphTimeZone(graph),
    nodes,
    relationships: removeNodeFromRelationships(graph.relationships ?? [], nodeId),
    connections: deriveConnections(nodes, updatedAt, {
      strategy: options.connectionStrategy ?? DEFAULT_CONNECTION_STRATEGY,
      timeZone,
    }),
    updatedAt,
    updatedAtTimeZone: timeZone,
  };
}
